import HitLabListenResult from './HitLabListenResult';
import HitLabResultSharing from './HitLabResultSharing';
import { serviceCopy } from './hitLabServiceTerms';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import {
  HITLAB_SESSION_MESSAGE,
  buildHitLabAuthConfig,
  getHitLabApiMessage,
  isHitLabClientEnabled,
  isHttpsUrl,
  isHttpsSongUrl,
} from './hitLabUtils';
import { HITLAB_FUNNEL_EVENTS, recordHitLabEvent } from './hitLabTelemetry';
import HitLabPostSubmitBridge from './HitLabPostSubmitBridge';
import './hitLab.css';

const INITIAL_FORM = {
  artistName: '',
  cityRegion: '',
  contactEmail: '',
  contactPhone: '',
  songTitle: '',
  genreLane: '',
  releaseStatus: '',
  bestContactOrSocialUrl: '',
  whyThisSong: '',
  hookTimestamp: '',
  artistImageUrl: '',
  songCoverArtUrl: '',
  songPrivateUrl: '',
  cleanExplicit: '',
  originalWork: false,
  noCovers: false,
  noUnclearedSamples: false,
  noStolenBeat: false,
  producerRightsClear: false,
  featuresApproved: false,
  authorizedSubmitter: false,
  privateReviewPlayback: false,
  futurePublicPromotion: false,
  futureStreetDropConsideration: false,
  contactAboutOpportunities: false,
};

const REQUIRED_RIGHTS = [
  [
    'originalWork',
    'I own or control the master recording and the underlying song/composition rights, or I am authorized by those who do.',
  ],
  ['noCovers', 'This is not a cover.'],
  ['noUnclearedSamples', 'This song has no uncleared samples.'],
  ['noStolenBeat', 'Beat and producer rights are cleared.'],
  [
    'producerRightsClear',
    'Producer, beat, and collaborator permissions are clear for private HitLab review.',
  ],
  ['featuresApproved', 'All featured artists approved this submission.'],
  ['authorizedSubmitter', 'I am authorized to submit this song.'],
  ['privateReviewPlayback', 'I allow private HitLab review playback.'],
];

const HITLAB_INVITE_URL = 'https://www.joegalaxy.net/hitlab';
const HITLAB_SUBMITTED_SHARE_TEXT =
  'I submitted one record to JG HitLab. One record. One shot.';
const HITLAB_SUBMITTED_SHARE_MESSAGE =
  `${HITLAB_SUBMITTED_SHARE_TEXT}\n${HITLAB_INVITE_URL}`;

const RECEIPT_STATUSES = new Set([
  'uploaded_pending_rights_screen',
  'ready_for_curator_listen',
  'hitlab_candidate',
  'street_drop_candidate',
  'hold',
  'rejected',
  'approved_members_rotation',
]);

const STATUS_LABELS = {
  uploaded_pending_rights_screen: 'Rights screen pending',
  ready_for_curator_listen: 'Curator listen queued',
  hitlab_candidate: 'HitLab candidate',
  street_drop_candidate: 'Street Drop candidate',
  hold: 'On hold',
  rejected: 'Review complete',
  approved_members_rotation: 'Rotation approved',
};

const getStatusLabel = (status) => STATUS_LABELS[status] || 'In review';

const buildSummaryRows = (intake = {}) =>
  [
    ['Artist', intake.artistName],
    ['Song', intake.songTitle],
    ['Genre lane', intake.genreLane],
    ['City / region', intake.cityRegion],
    ['Release status', intake.releaseStatus],
    ['Hook timestamp', intake.hookTimestamp],
    ['Clean / explicit', intake.cleanExplicit],
    ['Current status', getStatusLabel(intake.status)],
  ].filter(([, value]) => String(value || '').trim());

export default function HitLabSubmit() {
  const { player } = useContext(AuthContext);
  const location = useLocation();
  const checkoutSuccess = useMemo(
    () => new URLSearchParams(location.search || '').get('checkout') === 'success',
    [location.search],
  );
  const authConfig = useMemo(
    () => buildHitLabAuthConfig(player),
    [player?.token],
  );
  const [loading, setLoading] = useState(true);
  const [intake, setIntake] = useState(null);
  const [requestedResult, setRequestedResult] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shareState, setShareState] = useState({ message: '', showFallback: false });
  const clientEnabled = isHitLabClientEnabled();

  const requestedVersion = new URLSearchParams(location.search || '').get('result');
  useEffect(() => {
    let active = true;
    setRequestedResult(null);
    if (!requestedVersion || !authConfig) return undefined;
    axiosConn.get(`hitlab/intake/results/${encodeURIComponent(requestedVersion)}`, authConfig)
      .then(response => { if (active) setRequestedResult(response.data.data.result); })
      .catch(err => { if (active) setError(getHitLabApiMessage(err, 'This released result is unavailable.')); });
    return () => { active = false; };
  }, [requestedVersion, authConfig]);
  const loadIntake = () => {
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      return Promise.resolve(null);
    }
    return axiosConn.get('hitlab/intake/me', authConfig).then((response) => {
      const next = response.data?.data?.intake || null;
      setIntake(next);
      setForm((current) => ({
        ...current,
        artistName: current.artistName || next?.artistName || '',
        cityRegion: current.cityRegion || next?.cityRegion || '',
        contactEmail: current.contactEmail || next?.contactEmail || '',
        contactPhone: current.contactPhone || next?.contactPhone || '',
        songTitle: current.songTitle || next?.songTitle || '',
        genreLane: current.genreLane || next?.genreLane || '',
        releaseStatus: current.releaseStatus || next?.releaseStatus || '',
        bestContactOrSocialUrl:
          current.bestContactOrSocialUrl || next?.bestContactOrSocialUrl || '',
        whyThisSong: current.whyThisSong || next?.whyThisSong || '',
        hookTimestamp: current.hookTimestamp || next?.hookTimestamp || '',
        artistImageUrl: current.artistImageUrl || next?.artistImageUrl || '',
        songCoverArtUrl: current.songCoverArtUrl || next?.songCoverArtUrl || '',
        cleanExplicit: current.cleanExplicit || next?.cleanExplicit || '',
      }));
      return next;
    });
  };

  useEffect(() => {
    if (!clientEnabled) {
      setLoading(false);
      return undefined;
    }
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      setLoading(false);
      return undefined;
    }
    let active = true;
    loadIntake()
      .catch((err) => {
        if (active) {
          setError(
            getHitLabApiMessage(
              err,
              'Artist intake status could not be loaded. Please try again.',
            ),
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authConfig, clientEnabled]);

  useEffect(() => {
    if (!checkoutSuccess || !authConfig || intake?.paymentStatus === 'paid') return undefined;
    let attempts = 0;
    const id = window.setInterval(() => {
      attempts += 1;
      loadIntake().catch(() => {});
      if (attempts >= 5) window.clearInterval(id);
    }, 3000);
    return () => window.clearInterval(id);
  }, [authConfig, checkoutSuccess, intake?.paymentStatus]);

  useEffect(() => {
    if (!clientEnabled || !checkoutSuccess) return;
    void recordHitLabEvent(HITLAB_FUNNEL_EVENTS.CHECKOUT_RETURNED_SUCCESS, {
      search: location.search,
      path: '/hitlab/submit',
    });
  }, [checkoutSuccess, clientEnabled, location.search]);

  const updateField = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const candidateResult = requestedResult || (!requestedVersion ? intake?.releasedListenResult : null);
  const displayedResult = ['earned_next_move', 'not_yet'].includes(candidateResult?.decision) ? candidateResult : null;
  const interimHold = intake?.serviceWindow?.interimHold ||
    (candidateResult?.decision === 'hold_second_listen' ? { followUpAt: null } : null);
  const canSubmit = intake?.paymentStatus === 'paid' && intake?.status === 'paid_pending_upload';
  const showReceipt =
    intake?.paymentStatus === 'paid' && RECEIPT_STATUSES.has(String(intake?.status || ''));
  useEffect(() => {
    if (!clientEnabled || !canSubmit) return;
    void recordHitLabEvent(HITLAB_FUNNEL_EVENTS.SUBMIT_VIEWED_PAID, {
      search: location.search,
      path: '/hitlab/submit',
      status: intake?.status,
    });
  }, [canSubmit, clientEnabled, intake?.status, location.search]);

  const shareHitLab = async () => {
    setShareState({ message: '', showFallback: false });
    const shareData = {
      title: 'JG HitLab Artist Intake',
      text: HITLAB_SUBMITTED_SHARE_TEXT,
      url: HITLAB_INVITE_URL,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setShareState({ message: 'JG HitLab link ready to share.', showFallback: false });
        return;
      }
    } catch (err) {
      // Fall through to clipboard copy when native share is unavailable or cancelled.
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(HITLAB_SUBMITTED_SHARE_MESSAGE);
        setShareState({ message: 'JG HitLab share copied.', showFallback: false });
        return;
      }
    } catch (err) {
      // Fall through to a visible copy field when clipboard copy is unavailable.
    }

    setShareState({
      message: 'Copy the JG HitLab share message below.',
      showFallback: true,
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      return;
    }
    if (!isHttpsSongUrl(form.songPrivateUrl)) {
      setError('Private song link must start with https://');
      return;
    }
    if (form.artistImageUrl.trim() && !isHttpsUrl(form.artistImageUrl)) {
      setError('Artist image URL must start with https://');
      return;
    }
    if (form.songCoverArtUrl.trim() && !isHttpsUrl(form.songCoverArtUrl)) {
      setError('Song cover art URL must start with https://');
      return;
    }
    const missingRight = REQUIRED_RIGHTS.find(([key]) => !form[key]);
    if (missingRight) {
      setError('Required rights confirmations must be accepted.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axiosConn.post('hitlab/intake/submit', form, authConfig);
      setIntake(response.data?.data?.intake || null);
      setSuccess('Song submitted. HitLab review status is now recorded.');
    } catch (err) {
      setError(
        getHitLabApiMessage(err, 'Submission could not be saved. Please try again.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="hitlab-page hitlab-workflow">
      <Helmet>
        <title>JG HitLab Song Submission</title>
      </Helmet>
      <section className="hitlab-shell hitlab-submit-shell">
        {!clientEnabled ? (
          <div className="hitlab-alert">Artist intake is not accepting submissions right now.</div>
        ) : loading ? (
          <div className="hitlab-alert">Loading payment status...</div>
        ) : !authConfig ? (
          <div className="hitlab-alert hitlab-alert-error">{HITLAB_SESSION_MESSAGE}</div>
        ) : showReceipt ? (
          <>
            {error && <p role="alert">{error}</p>}
            <div className="hitlab-workflow-copy hitlab-receipt-hero">
              <p className="hitlab-kicker">{displayedResult ? 'LISTEN RESULT RELEASED' : 'SUBMISSION RECEIVED'}</p>
              <h1>{displayedResult ? 'Your Listen Result is ready.' : "You're in the HitLab review path."}</h1>
              <p className="hitlab-lede">
                {displayedResult ? 'Your written review, selection decision and next step are available below.' : serviceCopy.resultTime}
              </p>
              {!displayedResult && <p>
                Your result will be available here in your JG account. We’ll email you when it is ready. {serviceCopy.clockRule}
              </p>}
            </div>

            {displayedResult && <div className="hitlab-paid-experience hitlab-private-result"><HitLabListenResult result={displayedResult} /><HitLabResultSharing result={displayedResult} authConfig={authConfig} /></div>}
            {!displayedResult && intake?.serviceWindow?.dueAt && <p>Review deadline: {new Date(intake.serviceWindow.dueAt).toLocaleString()}</p>}
            {!displayedResult && interimHold && <p role="status">
              Second listen in progress. Your 72-hour deadline still applies. Follow-up:{' '}
              {interimHold.followUpAt ? new Date(interimHold.followUpAt).toLocaleString() : 'being scheduled'}.
            </p>}
            {intake?.serviceWindow?.blocker && <p role="alert">{intake.serviceWindow.blocker} · Follow-up: {new Date(intake.serviceWindow.followUpAt).toLocaleString()}</p>}
            <div className="hitlab-receipt-grid">
              <article className="hitlab-receipt-card">
                <p className="hitlab-kicker">RECEIPT</p>
                <h2>Submission receipt</h2>
                <dl className="hitlab-summary-list">
                  {buildSummaryRows(intake).map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{displayedResult && label === 'Current status' ? 'Listen Result released' : value}</dd>
                    </div>
                  ))}
                </dl>
              </article>

              {!displayedResult && <article className="hitlab-receipt-card">
                <p className="hitlab-kicker">NEXT</p>
                <h2>What happens next</h2>
                <ul className="hitlab-next-list">
                  <li>HitLab checks the submitted link and rights confirmations.</li>
                  <li>Keep your private link active. After your valid submission is processed and heard, you will receive your JG HitLab Listen Result.</li>
                  <li>The final result will make the next move clear: earned next move or not yet. A second-listen hold remains in progress and does not extend your deadline.</li>
                </ul>
                <p className="hitlab-receipt-note">
                  Placement cannot be bought. The record has to earn it.
                </p>
              </article>}

              {displayedResult ? <Link to="/home">Explore JG Universe</Link> : <HitLabPostSubmitBridge
                onShareHitLab={shareHitLab}
                shareFallbackMessage={HITLAB_SUBMITTED_SHARE_MESSAGE}
                shareState={shareState}
              />}
            </div>
          </>
        ) : !canSubmit ? (
          <div className="hitlab-alert">
            <strong>
              {intake?.paymentStatus === 'paid'
                ? 'Submission status unavailable'
                : 'Payment pending'}
            </strong>
            <span>
              {intake?.paymentStatus === 'paid'
                ? ' We could not confirm the current submission step. Refresh or contact HitLab if this continues.'
                : ' We are waiting for Stripe confirmation before song submission opens.'}
            </span>
            {intake?.paymentStatus !== 'paid' ? (
              <Link to="/hitlab/intake">Return to intake checkout</Link>
            ) : null}
          </div>
        ) : (
          <form className="hitlab-form" onSubmit={submit}>
            <div className="hitlab-workflow-copy">
              <p className="hitlab-kicker">SONG SUBMISSION</p>
              <h1>Send the record you would bet on.</h1>
              <p className="hitlab-lede">
                Use a private HTTPS link that Joe Galaxy and HitLab reviewers can open.
              </p>
              <p>
                {serviceCopy.resultTime}
              </p>
              <p>
                Keep your private link active and make sure it opens without
                requesting access.
              </p>
              <p>
                Valid submissions require original music and rights
                confirmations clear enough for review.
              </p>
            </div>
            {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
            {success ? <div className="hitlab-alert hitlab-alert-success">{success}</div> : null}
            <div className="hitlab-form-grid">
              <label>
                Artist name
                <input name="artistName" maxLength="120" value={form.artistName} onChange={updateField} required />
              </label>
              <label>
                City / region
                <input name="cityRegion" maxLength="120" value={form.cityRegion} onChange={updateField} required />
              </label>
              <label>
                Contact email override
                <input name="contactEmail" type="email" maxLength="254" value={form.contactEmail} onChange={updateField} />
              </label>
              <label>
                Contact phone (optional)
                <input name="contactPhone" maxLength="40" value={form.contactPhone} onChange={updateField} />
              </label>
              <label>
                Song title
                <input name="songTitle" maxLength="160" value={form.songTitle} onChange={updateField} required />
              </label>
              <label>
                Genre lane
                <input name="genreLane" maxLength="80" value={form.genreLane} onChange={updateField} required />
              </label>
              <label>
                Release status
                <input name="releaseStatus" maxLength="80" value={form.releaseStatus} onChange={updateField} placeholder="Unreleased, released, demo, mastered" />
              </label>
              <label>
                Best contact or social URL
                <input name="bestContactOrSocialUrl" maxLength="500" value={form.bestContactOrSocialUrl} onChange={updateField} />
              </label>
              <label className="hitlab-form-wide">
                Private HTTPS song link
                <input name="songPrivateUrl" maxLength="2048" value={form.songPrivateUrl} onChange={updateField} placeholder="https://..." required />
                <span className="hitlab-field-helper">
                  Paste a private link reviewers can open, such as a private SoundCloud, Dropbox, Google Drive, Box, or Cloudinary link. Make sure it opens without requesting access.
                </span>
              </label>
              <label>
                Hook timestamp
                <input name="hookTimestamp" maxLength="32" value={form.hookTimestamp} onChange={updateField} placeholder="0:42" />
              </label>
              <label>
                Clean / explicit
                <input name="cleanExplicit" maxLength="40" value={form.cleanExplicit} onChange={updateField} />
              </label>
              <label className="hitlab-form-wide">
                Artist image URL
                <input name="artistImageUrl" maxLength="2048" value={form.artistImageUrl} onChange={updateField} placeholder="https://..." />
                <span className="hitlab-field-helper">
                  Optional now. Required later for Joe Galaxy Radio Rotation, Hit Radar display, public artist cards, or promotional use.
                </span>
              </label>
              <label className="hitlab-form-wide">
                Song cover art URL
                <input name="songCoverArtUrl" maxLength="2048" value={form.songCoverArtUrl} onChange={updateField} placeholder="https://..." />
                <span className="hitlab-field-helper">
                  Optional now. Required later for Joe Galaxy Radio Rotation, Hit Radar display, public artist cards, or promotional use.
                </span>
              </label>
              <label className="hitlab-form-wide">
                Why this song
                <textarea name="whyThisSong" maxLength="1000" value={form.whyThisSong} onChange={updateField} />
              </label>
            </div>

            <fieldset className="hitlab-rights">
              <legend>Rights confirmations</legend>
              {REQUIRED_RIGHTS.map(([key, label]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    name={key}
                    checked={form[key]}
                    onChange={updateField}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </fieldset>

            <fieldset className="hitlab-rights hitlab-rights-optional">
              <legend>Optional future permissions</legend>
              <label>
                <input
                  type="checkbox"
                  name="futurePublicPromotion"
                  checked={form.futurePublicPromotion}
                  onChange={updateField}
                />
                <span>Contact me separately about possible public promotion.</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  name="futureStreetDropConsideration"
                  checked={form.futureStreetDropConsideration}
                  onChange={updateField}
                />
                <span>Contact me separately about possible street-drop consideration.</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  name="contactAboutOpportunities"
                  checked={form.contactAboutOpportunities}
                  onChange={updateField}
                />
                <span>Contact me about future JG music opportunities.</span>
              </label>
            </fieldset>

            <button type="submit" className="hitlab-primary-btn" disabled={submitting}>
              {submitting ? 'Submitting song...' : 'Submit song for HitLab review'}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
